import parse from "html-react-parser"
import classnames from "classnames"

import {
  IftttAiLogoBackground,
  IftttAiLogoVariant,
  iftttAiLaunch,
  iftttAiLogoSvg,
  lockupLogoSvg,
} from "shared/utils/ifttt_logo"
import { formatCredits } from "shared/utils/credits"
import { tierBadge, tierDisplayName } from "shared/utils/tiers"

import styles from "./plan_summary.module.scss"

export const formatCreditsPerMonth = (creditsPerMonth: number) => `${formatCredits(creditsPerMonth)} credits / month`

const svgAspectRatio = (svg: string) => {
  const viewBox = svg
    .match(/viewBox="([^"]+)"/)?.[1]
    .split(/\s+/)
    .map(Number)
  return viewBox?.[2] && viewBox[3] ? viewBox[2] / viewBox[3] : 1
}

// The Starter panel is lemon, so it takes the on-light logo and text; the Pro and Pro+
// panels are dark.
export const planPanelBackground = (tier: string): IftttAiLogoBackground =>
  tier === "credits_starter" ? "light" : "dark"

interface PlanLogoProps {
  tier: string
  logo?: IftttAiLogoVariant
  on?: IftttAiLogoBackground
  fill?: boolean
  className?: string
}

// The logo next to a plan badge drops the ".ai". `fill` stretches the logo and badge across
// the container at equal heights: each grows in proportion to its aspect ratio.
export const PlanLogo = ({ tier, logo = "color", on = "light", fill = false, className }: PlanLogoProps) => {
  const logoSvg = iftttAiLaunch() ? iftttAiLogoSvg({ variant: logo, on, ai: false }) : lockupLogoSvg(on)
  const badge = tierBadge(tier, on === "dark" ? "white" : "default")
  const fillStyle = (svg: string) => (fill ? { flexGrow: svgAspectRatio(svg) } : undefined)

  return (
    <div className={classnames(styles["plan-logo"], { [styles["plan-logo-fill"]]: fill }, className)}>
      <span style={fillStyle(logoSvg)}>{parse(logoSvg)}</span>
      <span role="img" aria-label={`${tierDisplayName(tier)} badge`} style={fillStyle(badge)}>
        {parse(badge)}
      </span>
    </div>
  )
}

interface Props {
  tier: string
  creditsPerMonth: number | null
  features: string[]
}

const PlanSummary = ({ tier, creditsPerMonth, features }: Props) => {
  const on = planPanelBackground(tier)

  return (
    <section className={classnames(styles["plan-summary"], styles[`plan-summary-on-${on}`])}>
      <div>
        <PlanLogo tier={tier} logo="mono" on={on} fill />
        {creditsPerMonth !== null && (
          <div className={styles["credits-allowance"]}>{formatCreditsPerMonth(creditsPerMonth)}</div>
        )}
      </div>

      {features.length > 0 && (
        <div>
          <div className={styles["benefits-title"]}>{tierDisplayName(tier)} plans include</div>
          <ul className={styles["benefits"]}>
            {features.map(feature => (
              <li key={feature}>{feature}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

export default PlanSummary
